// /app/research/done — thank-you page for completed research checkout.
//
// Lands here either:
//   - From a successful Stripe.confirmPayment (`?pi=<paymentIntentId>&status=succeeded`),
//   - From a Stripe redirect-based payment method (e.g. US bank, Klarna)
//     coming back with `?payment_intent=<pi>&payment_intent_client_secret=...`.
//
// We clear the research items from the Application Tray and show a
// branded confirmation. The full order detail lives on the BE under
// `GET /consumer/research/orders` (out of scope for v1 surfacing,
// path reserved per the BE contract §6).
import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Logo from "@/components/Logo";
import Button from "@/components/Button";
import { Eyebrow, Headline } from "@/components/Editorial";
import { useApplicationTray } from "@/lib/applicationTray";
import { C } from "@/lib/brand";

const NAVY_DEEP = "#0F1B2A";
const AMBER = "#E8C66B";

export default function ResearchDone() {
  const [searchParams] = useSearchParams();
  const pi = searchParams.get("pi") || searchParams.get("payment_intent") || "";
  const tray = useApplicationTray();

  // Clear the research items from the tray now that payment has
  // resolved. We use useMemo to capture the snapshot once and avoid
  // re-clearing on every re-render. BE will reconcile the real order
  // state via webhook regardless.
  const researchSnapshot = useMemo(() => tray.researchItems || [], []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    researchSnapshot.forEach((it) => tray.removeResearch(it.sku, it.variant));
    // Intentionally NOT including `tray` in deps — its identity changes
    // on every mutation and would re-trigger the loop forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="research-done-page" style={{ minHeight: "100vh", background: C.cream }}>
      <header style={{ background: NAVY_DEEP, padding: "18px 22px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Logo light size={20} />
        <span
          style={{
            fontFamily: '"JetBrains Mono", "IBM Plex Mono", ui-monospace, monospace',
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: "0.25em",
            color: AMBER,
            textTransform: "uppercase",
          }}
        >
          ▪ ORDER CONFIRMED
        </span>
      </header>

      <section style={{ padding: "72px 24px", maxWidth: 720, margin: "0 auto" }}>
        <Eyebrow color={AMBER}>★ THANK YOU</Eyebrow>
        <Headline before="YOUR ORDER" italic="is in." color={NAVY_DEEP} italicColor={AMBER} size={56} className="mt-4" />
        <p style={{ marginTop: 18, fontSize: 16, color: C.warmGray, lineHeight: 1.65, maxWidth: 560 }}>
          We've received your payment and your order has been queued for fulfilment.
          You'll get an email confirmation shortly with batch details and a tracking number once your shipment leaves the warehouse.
        </p>
        {pi && (
          <div
            data-testid="research-done-ref"
            style={{
              marginTop: 28,
              padding: "14px 18px",
              background: "#FFFFFF",
              border: `1px solid ${C.warmGray4}`,
              fontFamily: '"JetBrains Mono", "IBM Plex Mono", ui-monospace, monospace',
              fontSize: 11,
              letterSpacing: "0.18em",
              color: NAVY_DEEP,
              textTransform: "uppercase",
            }}
          >
            <div style={{ opacity: 0.55, marginBottom: 6 }}>REFERENCE</div>
            <div style={{ wordBreak: "break-all" }}>{pi}</div>
          </div>
        )}

        <div style={{ marginTop: 40, display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Link to="/research" data-testid="research-done-back-to-catalogue-link" style={{ textDecoration: "none" }}>
            <Button variant="primary" testId="research-done-back-to-catalogue-btn">
              BACK TO CATALOGUE →
            </Button>
          </Link>
          <Link to="/app" data-testid="research-done-back-to-home-link" style={{ textDecoration: "none" }}>
            <Button variant="ghost" testId="research-done-back-to-home-btn">
              GO TO MY PLAN
            </Button>
          </Link>
        </div>

        <p style={{ marginTop: 64, fontSize: 12, color: C.warmGray, lineHeight: 1.6, maxWidth: 560 }}>
          All research-product sales are final. For research purposes only. Not for human consumption.
        </p>
      </section>
    </div>
  );
}
